import Table from "../../../components/table/table";
import { useAppSelector } from "../../../hooks";
import {
  CheckRoleBasedPermission,
  permission,
  services,
} from "../../../utility/checkRoleBasedPermission";
import { Button, Chip } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { ReportItem } from "../../../types";
import Classes from "./reportServiceNew.module.css";
import ReportItemsClasses from "./reportItems.module.css";
import { formatDuration, intervalToDuration } from "date-fns";
import { setReportItemsData } from "../../../store/reportItemsSlice";
import { useState } from "react";
import { DisplayDialog } from "../../../components/displayDialog";

export default function ReportItems() {
  const reportItemsData = useAppSelector((state) => state.reportItemsData);
  const auth = useAppSelector((state) => state?.auth);
  const navigate = useNavigate();
  const { Id } = useParams();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState<JSX.Element>(
    <p>This is an example content inside the dialog.</p>
  );
  const [dialogTitle, setDialogTitle] = useState<string>("Dialog");

  const handleOpen = ({
    dialog_name = "tags",
    row,
  }: {
    dialog_name?: "tags" | "errorMessages";
    row: ReportItem;
  }) => {
    if (dialog_name === "tags") {
      const tags = row?.tags;
      setDialogContent(
        <div className={ReportItemsClasses.TagsContainer}>
          {tags?.map((tag, index) => (
            <div key={index} className={ReportItemsClasses.TagItem}>
              <strong className={ReportItemsClasses.TagName}>
                {tag.tag_name}:
              </strong>{" "}
              <span className={ReportItemsClasses.TagValue}>
                {tag.tag_value}
              </span>
            </div>
          ))}
        </div>
      );
      setDialogTitle(`Tags for Report Item Id: ${row?.id}`);
    } else if (dialog_name === "errorMessages") {
      const errorMessages = row?.error_messages;
      setDialogContent(
        <div className={ReportItemsClasses.ErrorMessagesContainer}>
          {errorMessages?.map((message, index) => (
            <div key={index} className={ReportItemsClasses.ErrorMessageItem}>
              <p>{message}</p>
            </div>
          ))}
        </div>
      );
      setDialogTitle(`Error Messages for Report Item Id: ${row?.id}`);
    }
    setDialogOpen(true);
  };
  const handleClose = () => {
    setDialogOpen(false);
  };

  const DurationRenderCell = (row: ReportItem) => {
    const duration = intervalToDuration({
      start: 0,
      end: (row?.duration || 0) * 1000,
    });
    const formattedDuration = formatDuration(duration, {
      format: ["hours", "minutes", "seconds"],
    });

    return (
      <p className={Classes.DurationContent}>{formattedDuration || "0s"}</p>
    );
  };

  const CreatedByRenderCell = (row: ReportItem) => {
    return (
      <Chip
        label={`${row?.created_by_name}`}
        component="a"
        onClick={(event) => {
          event.stopPropagation();
          CheckRoleBasedPermission(
            auth?.user,
            services.users,
            permission.read
          ) && navigate(`/users/${row?.created_by}`);
        }}
        clickable
      />
    );
  };

  const TagsCell = (row: ReportItem) => {
    return (
      <Button
        variant="contained"
        color="primary"
        onClick={(event) => {
          event.stopPropagation();
          handleOpen({ dialog_name: "tags", row });
        }}
        disabled={!row?.tags || row?.tags?.filter(Boolean)?.length === 0}
      >
        Show Tags
      </Button>
    );
  };

  const ErrorMessagesCell = (row: ReportItem) => {
    return (
      <Button
        variant="contained"
        color="primary"
        onClick={(event) => {
          event.stopPropagation();
          handleOpen({ dialog_name: "errorMessages", row });
        }}
        disabled={
          !row?.error_messages ||
          row?.error_messages?.filter(Boolean)?.length === 0
        }
      >
        Show Errors
      </Button>
    );
  };

  const columnVisibilityModel = {
    report_id: false,
    created_at: false,
    table_name: false,
    table_pk_id: false,
  };
  type params = { row: ReportItem };
  let columns = [
    {
      field: "report_id",
      headerName: "Report Id",
      flex: 1,
      headerClassName: "super-app-theme--header",
      filterable: false,
      sortable: false,
      type: "number",
    },
    {
      field: "id",
      headerName: "Report Item Id",
      flex: 1,
      headerClassName: "super-app-theme--header",
      filterable: true,
      sortable: false,
      type: "number",
    },
    {
      field: "table_name",
      headerName: "Table Name",
      flex: 1,
      headerClassName: "super-app-theme--header",
      filterable: false,
      sortable: false,
      type: "enum",
      tableName: "report_item",
      columnName: "table_name",
    },
    {
      field: "table_pk_id",
      headerName: "Table PK Id",
      flex: 1,
      headerClassName: "super-app-theme--header",
      filterable: false,
      sortable: false,
    },
    {
      field: "identifier_name",
      headerName: "Identifier Name",
      flex: 1,
      headerClassName: "super-app-theme--header",
      sortable: false,
      type: "enum",
      tableName: "report_item",
      columnName: "identifier_name",
    },
    {
      field: "identifier_value",
      headerName: "Identifier Value",
      flex: 1.4,
      headerClassName: "super-app-theme--header",
      sortable: false,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      headerAlign: "center",
      align: "center",
      headerClassName: "super-app-theme--header",
      sortable: false,
      type: "enum",
      tableName: "report_item",
      columnName: "status",
    },
    {
      field: "duration",
      headerName: "Duration",
      flex: 1,
      headerClassName: "super-app-theme--header",
      filterable: false,
      sortable: false,
      renderCell: (params: params) => <DurationRenderCell {...params?.row} />,
      customRenderCell: DurationRenderCell,
      type: "number",
    },
    {
      field: "created_at",
      headerName: "Started At",
      flex: 1.1,
      headerClassName: "super-app-theme--header",
      sortable: false,
    },
    {
      field: "updated_at",
      headerName: "Last Updated At",
      flex: 1.1,
      headerClassName: "super-app-theme--header",
      sortable: false,
    },
    {
      field: "created_by_name",
      headerName: "Created By",
      flex: 1.1,
      headerClassName: "super-app-theme--header",
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params: params) => <CreatedByRenderCell {...params?.row} />,
      customRenderCell: CreatedByRenderCell,
      type: "enum",
      tableName: "admins",
      columnName: "name",
    },
    {
      field: "tags",
      headerName: "Tags",
      flex: 1.4,
      headerClassName: "super-app-theme--header",
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params: params) => <TagsCell {...params?.row} />,
      customRenderCell: TagsCell,
    },
    {
      field: "errorMessages",
      headerName: "Error Messages",
      flex: 1.4,
      headerClassName: "super-app-theme--header",
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params: params) => <ErrorMessagesCell {...params?.row} />,
      customRenderCell: ErrorMessagesCell,
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataPosition={"reportItems"}
        endpoint={`/report/reports/${Id}`}
        rowId={"id"}
        checkBoxSelection={false}
        service="reportService"
        setData={setReportItemsData}
        data={reportItemsData}
        mobileResponsive={true}
        columnVisibilityModel={columnVisibilityModel}
        otherExports={[
          {
            label: "Uploaded File",
            endpoint: `/report/export-uploaded-file/${Id}`,
            isActive: reportItemsData?.uplodedFileKey ? true : false,
            exportFileName: `Uploaded File - ${Id}`, 
            successMessage: "File downloaded successfully",
          },
          {
            label: "Report Items",
            endpoint: `/report/reports/${Id}`,
            params: {
              isExport: true,
            },
            dataPosition: "reportItems",
            exportFileName: `Report Items - ${Id}`, 
            isActive: reportItemsData?.reportItems?.length > 0 ? true : false,
            successMessage: "File downloaded successfully",
          },
          {
            label: "Error Items",
            endpoint: `/report/export-error-items/${Id}`,
            params: {
              isExport: true,
            },
            dataPosition: "errorItems",
            exportFileName: `Error Items - ${Id}`, 
            isActive: (reportItemsData?.uplodedFileKey && reportItemsData?.reportItems?.filter((reportItem)=>{
              return reportItem?.status === "Failed" || reportItem?.error_messages?.length > 0
            })?.length > 0) ? true : false,
            successMessage: "File downloaded successfully",
          },
        ]}
        clickable={true}
        onRowClickRedirectUrl={(row: ReportItem) => {
          if (row?.table_name === "products" && row?.table_pk_id) {
            const url = `/products/${row["table_pk_id"]}`;
            return url;
          }
          return undefined;
        }}
      />
      <DisplayDialog
        open={dialogOpen}
        onClose={handleClose}
        title={dialogTitle}
      >
        {dialogContent}
      </DisplayDialog>
    </>
  );
}
